import React, {useEffect, useMemo, useRef, useState} from "react";

export type SearchableSelectOption = {
    value: string;
    label: string;
};

type SearchableSelectProps = {
    value: string;
    options: SearchableSelectOption[];
    onChange: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    invalid?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
};

// Accessible, filterable dropdown that replaces the native <select> so the
// user can type to narrow long option lists (e.g. Model, Series).
function SearchableSelect({
    value,
    options,
    onChange,
    onBlur,
    disabled = false,
    invalid = false,
    placeholder = "Select\u2026",
    searchPlaceholder = "Search\u2026",
}: Readonly<SearchableSelectProps>) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const selectedLabel = useMemo(
        () => options.find((option) => option.value === value)?.label ?? "",
        [options, value]
    );

    const filtered = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (term === "") {
            return options;
        }
        return options.filter((option) => option.label.toLowerCase().includes(term));
    }, [options, query]);

    const closeMenu = () => {
        setOpen(false);
        onBlur?.();
    };

    // Close and fire blur when the user clicks outside the component.
    useEffect(() => {
        if (!open) {
            return;
        }
        const handlePointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                closeMenu();
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    // Reset the query and focus the search box each time the panel opens.
    useEffect(() => {
        if (open) {
            setQuery("");
            setActiveIndex(0);
            globalThis.setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [open]);

    // Keep the keyboard-highlighted option scrolled into view.
    useEffect(() => {
        if (!open || !listRef.current) {
            return;
        }
        const active = listRef.current.querySelector<HTMLElement>(".is-active");
        active?.scrollIntoView({block: "nearest"});
    }, [activeIndex, open]);

    const toggleMenu = () => {
        if (disabled) {
            return;
        }
        setOpen((previous) => !previous);
    };

    const selectOption = (optionValue: string) => {
        onChange(optionValue);
        setOpen(false);
        onBlur?.();
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === "Enter") {
            event.preventDefault();
            const option = filtered[activeIndex];
            if (option) {
                selectOption(option.value);
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            closeMenu();
        }
    };

    return (
        <div
            ref={rootRef}
            className={`vin-select${open ? " is-open" : ""}${invalid ? " vin-update-invalid" : ""}${disabled ? " is-disabled" : ""}`}
        >
            <button
                type="button"
                className="vin-select-control"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={toggleMenu}
            >
                <span className={`vin-select-value${selectedLabel ? "" : " is-placeholder"}`}>
                    {selectedLabel || placeholder}
                </span>
                <i className="vin-select-caret" aria-hidden="true"/>
            </button>

            {open && (
                <div className="vin-select-menu">
                    <div className="vin-select-search">
                        <input
                            ref={searchRef}
                            type="text"
                            value={query}
                            placeholder={searchPlaceholder}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setActiveIndex(0);
                            }}
                            onKeyDown={handleSearchKeyDown}
                        />
                    </div>
                    <div ref={listRef} className="vin-select-list">
                        <button
                            type="button"
                            className="vin-select-option vin-select-placeholder"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption("")}
                        >
                            {placeholder}
                        </button>
                        {filtered.length === 0 ? (
                            <div className="vin-select-empty">No matches found</div>
                        ) : (
                            filtered.map((option, index) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`vin-select-option${option.value === value ? " is-selected" : ""}${index === activeIndex ? " is-active" : ""}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => selectOption(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SearchableSelect;
